import { ReceivablesLink } from '../receivables/Access';
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Table,
  Button,
  Form,
  InputGroup,
  Spinner,
  Alert,
  Badge,
} from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ReactPaginate from "react-paginate";
import { authFetch } from "../utils/authFetch";

const toISODate = (d) => {
  if (!d) return null;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

function formatDateRo(val) {
  if (!val) return "";
  const s = String(val);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return s;
}

export default function IesireList() {
  const { account_id } = useParams();
  const [searchParams] = useSearchParams();
  const bxsoldint = searchParams.get("bxsoldint");
  const accountId = Number(
    account_id ||
      searchParams.get("account_id") ||
      searchParams.get("cont_id") ||
      "",
  );

  const ctx = (() => {
    try {
      return JSON.parse(localStorage.getItem("iesire_ctx") || "null");
    } catch {
      return null;
    }
  })();

  const accountCode = ctx?.accountCode || "";
  const accountLabel = ctx?.accountLabel || "";
  const documentType =
    searchParams.get("tip_document") === "Aviz" ||
    (Number(ctx?.accountId) === accountId &&
      String(ctx?.accountCode || "").startsWith("418"))
      ? "Aviz"
      : null;

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(10);

  const [searchClient, setSearchClient] = useState("");
  const [docNo, setDocNo] = useState("");
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);

  const [sortBy, setSortBy] = useState("id");
  const [sortDir, setSortDir] = useState("desc");

  // Reset page when accountId or bxsoldint changes
  useEffect(() => {
    setSkip(0);
  }, [accountId, bxsoldint]);

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    p.set("skip", String(skip));
    p.set("limit", String(limit));
    p.set("sort_by", sortBy);
    p.set("sort_dir", sortDir);
    if (searchClient.trim()) p.set("client", searchClient.trim());
    if (docNo.trim()) p.set("doc_no", docNo.trim());
    if (dateFrom) p.set("date_from", toISODate(dateFrom));
    if (dateTo) p.set("date_to", toISODate(dateTo));
    if (bxsoldint === "1") p.set("bxsoldint", "1");
    return p.toString();
  }, [
    skip,
    limit,
    sortBy,
    sortDir,
    searchClient,
    docNo,
    dateFrom,
    dateTo,
    bxsoldint,
  ]);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const path = accountId
        ? `/iesire/iesire_list/${accountId}?${qs}`
        : `/iesire/?${qs}`;
      const data = await authFetch(path); // data e deja JSON
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`Sigur doriți să ștergeți ieșirea cu ID ${id}?`)) {
      return;
    }
    try {
      await authFetch(`/iesire/${id}`, {
        method: "DELETE",
      });
      load();
    } catch (e) {
      alert("Eroare la ștergere: " + (e?.message || e));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qs, accountId]);


  return (
    <Container className="py-3">
      <ReceivablesLink />
      <Row className="align-items-center mb-3">
        <Col>
          <div className="d-flex align-items-center gap-2">
            {accountCode && <Badge bg="secondary">{accountCode}</Badge>}
            {accountLabel && <span className="text-muted">{accountLabel}</span>}
            <h4 className="mb-0">
              {bxsoldint === "1"
                ? "Facturi initiale clienti"
                : "Iesiri (Facturi)"}
            </h4>
          </div>
        </Col>
        <Col xs="auto">
          <Button
            as={Link}
            to={(() => {
              const params = new URLSearchParams();
              if (accountId) params.set("cont_id", String(accountId));
              if (documentType) params.set("tip_document", documentType);
              if (bxsoldint === "1") params.set("bxsoldint", "1");
              const qs = params.toString();
              return `/iesire/new${qs ? `?${qs}` : ""}`;
            })()}
            variant="primary"
          >
            + Noua iesire
          </Button>
        </Col>
      </Row>

      {err && <Alert variant="danger">{err}</Alert>}

      <Row className="mb-3 g-2">
        <Col md={3}>
          <InputGroup>
            <Form.Control
              type="text"
              placeholder="Caută după client..."
              value={searchClient}
              onChange={(e) => {
                setSkip(0);
                setSearchClient(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") load();
              }}
            />
          </InputGroup>
        </Col>

        <Col md={2}>
          <InputGroup>
            <Form.Control
              value={docNo}
              onChange={(e) => {
                setSkip(0);
                setDocNo(e.target.value);
              }}
              placeholder="Nr. document..."
            />
          </InputGroup>
        </Col>

        <Col md={3}>
          <InputGroup>
            <InputGroup.Text>De la</InputGroup.Text>
            <DatePicker
              selected={dateFrom}
              onChange={(d) => {
                setSkip(0);
                setDateFrom(d);
              }}
              className="form-control"
              dateFormat="dd.MM.yyyy"
              isClearable
            />
          </InputGroup>
        </Col>

        <Col md={3}>
          <InputGroup>
            <InputGroup.Text>Până la</InputGroup.Text>
            <DatePicker
              selected={dateTo}
              onChange={(d) => {
                setSkip(0);
                setDateTo(d);
              }}
              className="form-control"
              dateFormat="dd.MM.yyyy"
              isClearable
            />
          </InputGroup>
        </Col>

        <Col md={1}>
          <Form.Select
            value={limit}
            onChange={(e) => {
              setSkip(0);
              setLimit(Number(e.target.value));
            }}
          >
            {[10, 20, 50, 100].map((x) => (
              <option key={x} value={x}>
                {x}
              </option>
            ))}
          </Form.Select>
        </Col>
      </Row>

      <Row className="g-2 mb-3">
        <Col md={6}>
          <InputGroup>
            <InputGroup.Text>Sort</InputGroup.Text>
            <Form.Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="id">ID</option>
              <option value="doc_no">Doc No</option>
              <option value="doc_date">Doc Date</option>
              <option value="value">Valoare</option>
              <option value="lines">Linii</option>
            </Form.Select>
            <Form.Select
              value={sortDir}
              onChange={(e) => setSortDir(e.target.value)}
            >
              <option value="desc">Desc</option>
              <option value="asc">Asc</option>
            </Form.Select>
          </InputGroup>
        </Col>
      </Row>

      <div className="d-flex justify-content-between mb-2">
        <div className="text-muted">
          {total} total • afisate {items.length} • skip {skip}
        </div>
        {loading && (
          <div className="text-muted">
            <Spinner size="sm" animation="border" /> loading…
          </div>
        )}
      </div>

      <Table striped bordered hover responsive size="sm">
        <thead>
          <tr>
            <th style={{ width: 90 }}>ID</th>
            <th style={{ width: 140 }}>Tip Document</th>
            <th style={{ width: 160 }}>Doc No</th>
            <th style={{ width: 150 }}>Doc Date</th>
            <th style={{ width: 150 }}>Data Scadenta</th>
            <th style={{ width: 140 }}>Client</th>
            <th style={{ width: 140 }} className="text-end">
              Total
            </th>
            <th style={{ width: 140 }} className="text-end">
              Sold
            </th>
            <th style={{ width: 120 }} className="text-end">
              Linii
            </th>
            <th style={{ width: 180, minWidth: 180 }}>Acțiuni</th>
          </tr>
        </thead>
        <tbody>
          {(items || []).map((it) => (
            <tr key={it.id}>
              <td>{it.id}</td>
              <td>
                <Badge bg="secondary">{it.tip_document || "Factura"}</Badge>
              </td>
              <td>
                {(it.doc_serie ? it.doc_serie + " " : "") + (it.doc_no || "")}
              </td>
              <td>{it.doc_date ? it.doc_date.substring(0, 10) : ""}</td>
              <td>
                {it.bxdatascadenta ? it.bxdatascadenta.substring(0, 10) : ""}
              </td>
              <td
                title={
                  it.contact_name || it.customer_name || it.contact_label || ""
                }
              >
                <div className="text-truncate" style={{ maxWidth: 200 }}>
                  {it.contact_name ||
                    it.customer_name ||
                    it.contact_label ||
                    "—"}
                </div>
              </td>
              <td className="text-end">
                {Number(it.bxtotalwtva || 0).toFixed(2)}{" "}
                {it.valuta_cod === "EUR" ? "EURO" : it.valuta_cod || "RON"}
              </td>
              <td className="text-end">
                {Number(
                  (it.bxtotalwtva || 0) - (it.bxsettled_value || 0),
                ).toFixed(2)}{" "}
                {it.valuta_cod === "EUR" ? "EURO" : it.valuta_cod || "RON"}
              </td>
              <td className="text-end">{it.linii_count ?? 0}</td>
              <td className="text-nowrap" style={{ minWidth: 180 }}>
                <Button
                  as={Link}
                  to={`/iesire/${it.id}`}
                  size="sm"
                  variant="outline-primary"
                  className="me-2"
                >
                  Editeaza
                </Button>
                <Button
                  size="sm"
                  variant="outline-danger"
                  onClick={() => handleDelete(it.id)}
                >
                  Sterge
                </Button>
              </td>
            </tr>
          ))}
          {!loading && (items || []).length === 0 && (
            <tr>
              <td colSpan={9} className="text-center text-muted py-3">
                Niciun rezultat
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      <ReactPaginate
        previousLabel={"← Înapoi"}
        nextLabel={"Înainte →"}
        pageCount={Math.max(1, Math.ceil(total / limit))}
        forcePage={Math.floor(skip / limit)}
        onPageChange={(event) => setSkip(event.selected * limit)}
        pageRangeDisplayed={3}
        marginPagesDisplayed={1}
        containerClassName={"pagination justify-content-center mt-3"}
        pageClassName={"page-item"}
        pageLinkClassName={"page-link"}
        previousClassName={"page-item"}
        previousLinkClassName={"page-link"}
        nextClassName={"page-item"}
        nextLinkClassName={"page-link"}
        activeClassName={"active"}
        breakClassName={"page-item"}
        breakLinkClassName={"page-link"}
        renderOnZeroPageCount={null}
      />
    </Container>
  );
}
